import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Trash2 } from 'lucide-react';
import type { DealPaymentInstallment } from '../../lib/types';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { BILLING_PERIOD_LABEL, BILLING_PERIOD_SUFFIX } from '../../lib/utils/billingPrices';

interface PaymentScheduleCalculatorProps {
  serviceAmount: number;
  existingInstallments: DealPaymentInstallment[];
  onSave: (installments: DealPaymentInstallment[]) => void;
  onClose: () => void;
  /**
   * Tipo del servizio. Determina:
   *  - se l'importo ha un periodo (ricorrente) o è una tantum
   *  - il significato delle rate: sui ricorrenti si dividono i pagamenti
   *    DEL SINGOLO periodo, non il totale × durata.
   */
  serviceType?: 'monthly' | 'annual' | 'one_time' | string | null;
  /**
   * Periodo di fatturazione della riga in mesi (metadata.billing_interval_months).
   * Serve a chiamare l'importo col suo nome: su una riga trimestrale da 1.470 €
   * scrivere "/mese" è un numero giusto con l'etichetta sbagliata, e finisce in
   * un documento che legge il cliente. Default: 12 sugli annuali, 1 sul resto,
   * come billingMonthsOf in EnhancedDealServiceSelector.
   */
  billingMonths?: number;
  /**
   * Data di accettazione (deal.signed_at). Quando valorizzata viene usata come
   * "giorno 0" stabile per le rate di tipo relative/fixed: senza questo, le
   * rate riaperte su offerta accettata shiftano in avanti perché `new Date()`
   * ricalcola da oggi (R05).
   */
  referenceDate?: string | null;
}

export function PaymentScheduleCalculator({
  serviceAmount,
  existingInstallments,
  onSave,
  onClose,
  serviceType,
  billingMonths,
  referenceDate
}: PaymentScheduleCalculatorProps) {
  console.log('🔄 Initializing PaymentScheduleCalculator:', {
    serviceAmount,
    existingInstallments,
    referenceDate
  });

  const { settings } = useSettingsStore();
  const showPaymentDueDate = settings?.show_payment_due_date ?? false;

  // Data base per i calcoli "relative" / "fixed":
  //  - se l'offerta è accettata → signed_at (referenceDate)
  //  - altrimenti → oggi
  const baseDate = referenceDate ? new Date(referenceDate) : new Date();
  const todayISO = baseDate.toISOString().split('T')[0];

  // Determina la modalita' scadenza di un installment esistente
  // Finup R21: 'tbd' = data da destinarsi (awaiting_approval), saltata dalla
  // coda di fatturazione automatica finché l'utente non la sblocca.
  const getDateMode = (inst: DealPaymentInstallment): 'date' | 'relative' | 'fixed' | 'tbd' => {
    if (inst.awaiting_approval) return 'tbd';
    if (inst.fixedDate) return 'date';
    if (inst.fixedDayOfMonth) return 'fixed';
    return 'relative';
  };

  const [installments, setInstallments] = useState<DealPaymentInstallment[]>(
    existingInstallments.length > 0
      ? existingInstallments.map(inst => ({
          ...inst,
          type: inst.type || 'fixed',
          value: inst.value || 0,
          amount: inst.amount || 0,
          daysAfterStart: inst.daysAfterStart || 0,
          description: inst.description || '',
          isConfigurable: inst.isConfigurable || false,
          timeUnit: inst.timeUnit || (inst.daysAfterStart > 0 && inst.daysAfterStart % 30 === 0 ? 'months' : 'days'),
          fixedDayOfMonth: inst.fixedDayOfMonth,
          nextMonth: inst.nextMonth,
          fixedDate: inst.fixedDate
        }))
      : [{
          type: 'fixed',
          value: serviceAmount,
          amount: serviceAmount,
          daysAfterStart: 0,
          description: 'Pagamento completo',
          isConfigurable: true,
          fixedDate: todayISO
        }]
  );

  // Function to calculate approximate due date for relative/fixed modes
  // Usa `baseDate` come "giorno 0" (= signed_at se l'offerta è accettata, altrimenti oggi).
  const calculateDueDate = (daysAfter: number, fixedDay?: number, nextMonth?: boolean): string => {
    const date = new Date(baseDate);
    if (fixedDay) {
      if (nextMonth) {
        date.setMonth(date.getMonth() + 1);
      }
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      date.setDate(Math.min(fixedDay, lastDayOfMonth));
    } else {
      date.setDate(date.getDate() + daysAfter);
    }
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    console.log('📊 Recalculating amounts for service amount:', serviceAmount);
    
    // Recalculate amounts when service amount changes
    setInstallments(prev => prev.map(inst => ({
      ...inst,
      amount: inst.type === 'percentage' 
        ? serviceAmount * (inst.value / 100)
        : inst.amount || 0
    })));
  }, [serviceAmount]);

  const addInstallment = () => {
    console.log('➕ Adding new installment');
    setInstallments([
      ...installments,
      {
        type: 'fixed',
        value: 0,
        amount: 0,
        daysAfterStart: 0,
        description: '',
        isConfigurable: true,
        timeUnit: 'days',
        fixedDate: todayISO
      }
    ]);
  };

  const removeInstallment = (index: number) => {
    console.log('🗑️ Removing installment at index:', index);
    setInstallments(prev => prev.filter((_, i) => i !== index));
  };

  const updateInstallment = (index: number, updates: Partial<DealPaymentInstallment>) => {
    console.log('🔄 Updating installment:', { index, updates });
    
    setInstallments(prev => prev.map((inst, i) => {
      if (i === index) {
        const updated = { ...inst, ...updates };
        
        // If type is percentage, recalculate amount
        if (updated.type === 'percentage' && typeof updated.value === 'number') {
          updated.amount = serviceAmount * (updated.value / 100);
/*           console.log('📊 Recalculated percentage amount:', {
            percentage: updated.value,
            amount: updated.amount
          }); */
        }
        
        // For fixed type, amount is the same as value
        if (updated.type === 'fixed' && typeof updates.value === 'number') {
          updated.amount = updates.value;
          console.log('💰 Set fixed amount:', updated.amount);
        }
        
        return updated;
      }
      return inst;
    }));
  };

  const calculateTotalScheduled = () => {
    const total = installments.reduce((sum, inst) => sum + (inst.amount || 0), 0);
/*     console.log('📊 Total scheduled amount:', total);
 */    return total;
  };

const handleSave = () => {
  console.log('💾 Saving payment schedule');

  // Ensure all required fields are filled
  const isValid = installments.every(inst => {
    const hasAmount = inst.type === 'fixed'
      ? (typeof inst.amount === 'number' && inst.amount > 0)
      : (typeof inst.value === 'number' && inst.value > 0);
    const mode = getDateMode(inst);
    // Finup R21: in modalità tbd la data è esplicitamente "da destinarsi",
    // bypassa il controllo data — l'utente la definirà più avanti.
    const hasDateInfo = mode === 'tbd' ? true
      : mode === 'date' ? !!inst.fixedDate
      : mode === 'fixed' ? !!inst.fixedDayOfMonth
      : typeof inst.daysAfterStart === 'number' && inst.daysAfterStart >= 0;
    return hasAmount && hasDateInfo;
  });

  if (!isValid) {
    console.error('❌ Invalid installment data - missing required fields');
    alert('Si prega di compilare tutti i campi obbligatori per ogni rata');
    return;
  }

  // Verify total matches service amount
  const total = calculateTotalScheduled();
  if (Math.abs(total - serviceAmount) > 0.01) {
    console.error('❌ Total mismatch:', {
      total,
      serviceAmount,
      difference: Math.abs(total - serviceAmount)
    });
    alert('Il totale delle rate deve essere uguale all\'importo del servizio');
    return;
  }

// Crea una copia profonda normalizzata delle installments
  const serializedInstallments = JSON.stringify(installments.map(inst => {
    const mode = getDateMode(inst);
    return {
      type: inst.type || 'fixed',
      value: typeof inst.value === 'number' ? inst.value : 0,
      amount: typeof inst.amount === 'number' ? inst.amount : 0,
      daysAfterStart: mode === 'relative' ? (inst.daysAfterStart || 0) : 0,
      description: inst.description || '',
      isConfigurable: inst.isConfigurable || false,
      timeUnit: mode === 'relative' ? (inst.timeUnit || 'days') : undefined,
      fixedDayOfMonth: mode === 'fixed' ? inst.fixedDayOfMonth : undefined,
      nextMonth: mode === 'fixed' ? inst.nextMonth : undefined,
      fixedDate: mode === 'date' ? inst.fixedDate : undefined,
      // Finup R21: preserva il flag attesa-approvazione (vero solo se mode='tbd')
      awaiting_approval: mode === 'tbd' ? true : false,
      // Scadenza PAGAMENTO fattura (distinta dall'emissione): data fissa o giorni dopo emissione
      payment_due_date: inst.payment_due_date || undefined,
      payment_terms_days: inst.payment_due_date ? undefined : (inst.payment_terms_days ?? 0),
    };
  }));
  
/*   console.log('💾 Installments serializzate:', serializedInstallments);
 */  
  // Passa le installments deserializzate
  onSave(JSON.parse(serializedInstallments));
  onClose();
};



  const remaining = serviceAmount - calculateTotalScheduled();
  const balanced = Math.abs(remaining) < 0.01;
  // Il periodo si legge dalla riga, non dal tipo di servizio: un canone mensile
  // fatturato ogni tre mesi è un importo TRIMESTRALE. Vocabolario condiviso con
  // il selettore di frequenza (BILLING_PERIOD_*), così la stessa riga si chiama
  // allo stesso modo ovunque.
  const isRecurring = serviceType === 'monthly' || serviceType === 'annual';
  const months = billingMonths && billingMonths > 0
    ? billingMonths
    : (serviceType === 'annual' ? 12 : 1);
  const periodSuffix = isRecurring ? (BILLING_PERIOD_SUFFIX[months] ?? '') : '';
  const amountLabel = isRecurring && BILLING_PERIOD_LABEL[months]
    ? `Importo ${BILLING_PERIOD_LABEL[months].toLowerCase()}`
    : 'Importo servizio';
  // Su un periodo lungo l'importo è grande e si legge come il totale del
  // contratto: dirlo esplicitamente evita rate costruite sulla cifra sbagliata.
  const periodHint = isRecurring && months > 1 && BILLING_PERIOD_SUFFIX[months]
    ? `Le rate dividono ${BILLING_PERIOD_SUFFIX[months].replace('/', 'un ')}, non l'intero contratto.`
    : null;

  return (
    <div className="flex flex-col h-full text-black dark:text-white">
      {/* Header: importi inline in stile Notion (no card heavy con shadow) */}
      <div className="px-5 pt-4 pb-3 border-b border-black/[0.06] dark:border-white/[0.08]">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
              {amountLabel}
            </div>
            <p className="text-[22px] font-semibold text-black dark:text-white tabular-nums">
              €{serviceAmount.toFixed(2)}
              {periodSuffix && (
                <span className="text-[12px] font-normal text-black/45 dark:text-white/45 ml-0.5">{periodSuffix}</span>
              )}
            </p>
            {periodHint && (
              <p className="text-[11px] text-black/45 dark:text-white/45 mt-0.5">{periodHint}</p>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
              Rimanente
            </div>
            <p className={`text-[22px] font-semibold tabular-nums ${
              balanced
                ? 'text-emerald-600 dark:text-emerald-400'
                : remaining < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-amber-600 dark:text-amber-400'
            }`}>
              €{remaining.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Sezione rate */}
      <div className="px-5 pt-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-[#E91E8C]">
          Rate di pagamento
        </div>
        <button
          type="button"
          onClick={addInstallment}
          className="inline-flex items-center gap-1 text-[12px] text-black/55 dark:text-white/55 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] px-1.5 py-0.5 rounded transition-colors"
        >
          <Plus className="h-3 w-3" />
          <span>Aggiungi rata</span>
        </button>
      </div>
      
      {/* Scrollable area */}
      <div className="overflow-y-auto flex-grow px-5 py-3">
        <div className="space-y-2">
          {installments.map((installment, index) => {
            const dateMode = getDateMode(installment);
            const estimatedDueDate = dateMode !== 'date'
              ? calculateDueDate(installment.daysAfterStart || 0, installment.fixedDayOfMonth, installment.nextMonth)
              : '';
            return (
            <div key={index} className="group rounded-md hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors px-2 py-2 -mx-2">
              {/* Riga titolo: numero rata + descrizione (contenteditable) +
                  delete a destra che appare in hover, stile Notion property */}
              <div className="flex items-center gap-2 mb-1">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-[11px] font-semibold bg-black/[0.05] dark:bg-white/[0.06] text-black/55 dark:text-white/55">
                  {index + 1}
                </span>
                <div
                  className="flex-1 min-w-0 text-[13px] font-medium text-black dark:text-white focus:outline-none px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.04] focus:bg-white dark:focus:bg-white/[0.06] cursor-text"
                  contentEditable
                  suppressContentEditableWarning={true}
                  onFocus={(e) => {
                    if (e.currentTarget.textContent === 'Senza nome') {
                      e.currentTarget.textContent = '';
                    }
                  }}
                  onBlur={(e) => {
                    const text = e.currentTarget.textContent?.trim() || '';
                    updateInstallment(index, { description: text });
                    if (!text) {
                      e.currentTarget.textContent = 'Senza nome';
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                >
                  {installment.description || 'Senza nome'}
                </div>
                <button
                  type="button"
                  onClick={() => removeInstallment(index)}
                  className="opacity-0 group-hover:opacity-100 text-black/45 hover:text-red-600 dark:text-white/45 dark:hover:text-red-400 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                  title="Rimuovi rata"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Property rows Notion-like: label fissa a sinistra (90px),
                  value sulla destra che si adatta. Una riga per property. */}
              <div className="space-y-0.5 ml-7">
                {/* Tipo */}
                <PropertyRow label="Tipo">
                  <select
                    className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded cursor-pointer"
                    value={installment.type}
                    onChange={(e) => updateInstallment(index, {
                      type: e.target.value as 'percentage' | 'fixed',
                      value: 0,
                      amount: 0
                    })}
                  >
                    <option value="fixed">Fisso</option>
                    <option value="percentage">Percentuale</option>
                  </select>
                </PropertyRow>

                {/* Valore (% o €) */}
                <PropertyRow label={installment.type === 'percentage' ? 'Percentuale' : 'Importo'}>
                  <div className="flex items-baseline gap-1">
                    <span className="text-[12px] text-black/45 dark:text-white/45 flex-shrink-0">
                      {installment.type === 'percentage' ? '%' : '€'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step={installment.type === 'percentage' ? '1' : '0.01'}
                      max={installment.type === 'percentage' ? '100' : undefined}
                      placeholder="0"
                      className="w-24 bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 hover:bg-black/[0.04] dark:hover:bg-white/[0.05] focus:bg-white dark:focus:bg-white/[0.06] px-1 -mx-1 rounded tabular-nums"
                      value={installment.type === 'percentage' ? installment.value || '' : installment.amount || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : 0;
                        if (installment.type === 'percentage') {
                          updateInstallment(index, {
                            value,
                            amount: (value / 100) * serviceAmount
                          });
                        } else {
                          updateInstallment(index, {
                            value,
                            amount: value
                          });
                        }
                      }}
                    />
                    {installment.type === 'percentage' && (
                      <span className="text-[11px] text-black/45 dark:text-white/45 tabular-nums">
                        ≈ €{((installment.value || 0) / 100 * serviceAmount).toFixed(2)}
                      </span>
                    )}
                  </div>
                </PropertyRow>

                {/* Scadenza: tipo + valore correlato */}
                <PropertyRow label="Scadenza">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <select
                      className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded cursor-pointer"
                      value={dateMode}
                      onChange={(e) => {
                        const mode = e.target.value;
                        if (mode === 'date') {
                          updateInstallment(index, { fixedDate: todayISO, fixedDayOfMonth: undefined, nextMonth: undefined, daysAfterStart: 0, awaiting_approval: false });
                        } else if (mode === 'fixed') {
                          updateInstallment(index, { fixedDayOfMonth: 15, fixedDate: undefined, nextMonth: false, daysAfterStart: 0, awaiting_approval: false });
                        } else if (mode === 'tbd') {
                          // Finup R21: data da destinarsi — la rata NON entra in coda
                          // fatturazione finché l'utente non la sblocca (Approva ed emetti).
                          updateInstallment(index, { fixedDate: undefined, fixedDayOfMonth: undefined, nextMonth: undefined, daysAfterStart: 0, awaiting_approval: true });
                        } else {
                          updateInstallment(index, { fixedDate: undefined, fixedDayOfMonth: undefined, nextMonth: undefined, daysAfterStart: 0, timeUnit: 'days', awaiting_approval: false });
                        }
                      }}
                    >
                      <option value="date">Data specifica</option>
                      <option value="relative">Giorni/Mesi dopo</option>
                      <option value="fixed">Giorno fisso</option>
                      <option value="tbd">Data da destinarsi</option>
                    </select>
                    {dateMode === 'tbd' && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                        In attesa di approvazione
                      </span>
                    )}

                    {dateMode === 'date' && (
                      <input
                        type="date"
                        className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded"
                        value={installment.fixedDate || ''}
                        onChange={(e) => updateInstallment(index, { fixedDate: e.target.value })}
                      />
                    )}
                    {dateMode === 'fixed' && (
                      <>
                        <select
                          className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded cursor-pointer"
                          value={installment.fixedDayOfMonth || 15}
                          onChange={(e) => updateInstallment(index, { fixedDayOfMonth: parseInt(e.target.value) })}
                        >
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>Giorno {i + 1}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={installment.nextMonth || false}
                            onChange={(e) => updateInstallment(index, { nextMonth: e.target.checked })}
                            className="w-3 h-3 rounded border-gray-300 text-[#E91E8C] focus:ring-[#E91E8C] focus:ring-1"
                          />
                          <span className="text-[11px] text-black/55 dark:text-white/55">Prossimo mese</span>
                        </label>
                      </>
                    )}
                    {dateMode === 'relative' && (
                      <div className="flex items-baseline gap-1">
                        <input
                          type="number"
                          min="0"
                          className="w-12 bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] focus:bg-white dark:focus:bg-white/[0.06] px-1 -mx-1 rounded tabular-nums"
                          value={installment.daysAfterStart !== undefined ? installment.daysAfterStart : ''}
                          onChange={(e) => updateInstallment(index, { daysAfterStart: parseInt(e.target.value) || 0 })}
                          placeholder="0"
                        />
                        <select
                          className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded cursor-pointer"
                          value={installment.timeUnit || 'days'}
                          onChange={(e) => {
                            const value = e.target.value as 'days' | 'months';
                            const currentValue = installment.daysAfterStart || 0;
                            const currentUnit = installment.timeUnit || 'days';
                            let newValue = currentValue;
                            if (value !== currentUnit) {
                              if (value === 'months' && currentUnit === 'days') {
                                newValue = Math.floor(currentValue / 30);
                              } else if (value === 'days' && currentUnit === 'months') {
                                newValue = currentValue * 30;
                              }
                            }
                            updateInstallment(index, { daysAfterStart: newValue, timeUnit: value });
                          }}
                        >
                          <option value="days">Giorni</option>
                          <option value="months">Mesi</option>
                        </select>
                      </div>
                    )}
                  </div>
                </PropertyRow>

                {/* Scadenza PAGAMENTO fattura — distinta dall'emissione qui sopra.
                    "Giorni dopo emissione" = termini di pagamento (es. 30gg); "Data fissa" = override.
                    Visibile solo se l'org ha attivato show_payment_due_date nelle impostazioni offerte. */}
                {showPaymentDueDate && (
                <PropertyRow label="Pagamento">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <select
                      className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded cursor-pointer"
                      value={installment.payment_due_date ? 'fixed' : 'days'}
                      onChange={(e) => {
                        if (e.target.value === 'fixed') {
                          updateInstallment(index, { payment_due_date: todayISO, payment_terms_days: undefined });
                        } else {
                          updateInstallment(index, { payment_due_date: undefined, payment_terms_days: installment.payment_terms_days ?? 0 });
                        }
                      }}
                    >
                      <option value="days">Giorni dopo emissione</option>
                      <option value="fixed">Data fissa</option>
                    </select>
                    {installment.payment_due_date ? (
                      <input
                        type="date"
                        className="bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] px-1 -mx-1 rounded"
                        value={installment.payment_due_date || ''}
                        onChange={(e) => updateInstallment(index, { payment_due_date: e.target.value, payment_terms_days: undefined })}
                      />
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <input
                          type="number"
                          min="0"
                          className="w-12 bg-transparent border-0 outline-none focus:ring-0 text-[13px] text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.05] focus:bg-white dark:focus:bg-white/[0.06] px-1 -mx-1 rounded tabular-nums"
                          value={installment.payment_terms_days !== undefined ? installment.payment_terms_days : 0}
                          onChange={(e) => updateInstallment(index, { payment_terms_days: parseInt(e.target.value) || 0, payment_due_date: undefined })}
                          placeholder="0"
                        />
                        <span className="text-[11px] text-black/55 dark:text-white/55">gg dopo emissione</span>
                      </div>
                    )}
                  </div>
                </PropertyRow>
                )}

                {/* Preview data stimata: nascosto per 'date' (gia' inline). */}
                {dateMode !== 'date' && estimatedDueDate && (
                  <PropertyRow label="Stimata">
                    <span className="text-[12px] text-black/55 dark:text-white/55">{estimatedDueDate}</span>
                  </PropertyRow>
                )}
              </div>
            </div>
          )})}
          {installments.length === 0 && (
            <p className="text-[12px] text-black/45 dark:text-white/45 text-center py-4">
              Nessuna rata. Clicca <span className="font-medium">Aggiungi rata</span> per iniziare.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center px-5 py-3 border-t border-black/[0.08] dark:border-white/[0.08] bg-[#F7F6F3] dark:bg-gray-900/60 mt-auto">
        <div className="text-[12px]">
          <span className="text-black/55 dark:text-white/55">Totale</span>{' '}
          <span className="font-semibold text-black dark:text-white tabular-nums">€{calculateTotalScheduled().toFixed(2)}</span>
          {Math.abs(calculateTotalScheduled() - serviceAmount) > 0.01 && (
            <span className="ml-2 text-red-600 dark:text-red-400 text-[11px]">
              (Mancanti €{(serviceAmount - calculateTotalScheduled()).toFixed(2)})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1 text-[12px] text-black/65 dark:text-white/65 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded transition-colors"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={Math.abs(calculateTotalScheduled() - serviceAmount) > 0.01}
            className="px-3 py-1 text-[12px] font-medium text-white bg-[#E91E8C] hover:bg-[#D11A7E] rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Salva piano
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Riga property Notion-like: label fissa a sinistra (90px) + valore.
 * Usata nella card rata del calculator.
 */
function PropertyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 min-h-[24px]">
      <span className="text-[11px] text-black/45 dark:text-white/45 w-[90px] flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
}